import { useMemo, useState } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../components/ui/command";
import { LenderTag, DisbursementTranche, RequirementType } from "../../types/deals";
import { AddDisbursementDialog } from "./AddDisbursementDialog";
import { ViewDisbursementDialog } from "./ViewDisbursementDialog";
import { Banknote, Plus, Search, Building2, Check, ChevronsUpDown, Eye, CheckCircle2, AlertTriangle, Clock, CreditCard } from "lucide-react";
import { parseISO, format, differenceInDays, addMonths, isAfter } from "date-fns";
import { cn } from "../../lib/utils";

interface DisbursementTabProps {
  lenders: LenderTag[];
  disbursements: DisbursementTranche[];
  onAddDisbursement: (disbursement: Omit<DisbursementTranche, "id">) => void;
  requirementType?: RequirementType;
}

export function DisbursementTab({ lenders, disbursements = [], onAddDisbursement, requirementType = "Leasing" }: DisbursementTabProps) {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [viewDisbursement, setViewDisbursement] = useState<DisbursementTranche | null>(null);
  const [selectedLenderFilter, setSelectedLenderFilter] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [lenderSearchOpen, setLenderSearchOpen] = useState(false);

  const lenderOptions = useMemo(() => {
    const uniqueLenders = new Map<string, { institutionId: string; name: string }>();
    disbursements.forEach((d) => {
      const lender = lenders.find((l) => l.institutionId === d.lenderId);
      if (lender && lender.institutionId && !uniqueLenders.has(lender.institutionId)) {
        uniqueLenders.set(lender.institutionId, { institutionId: lender.institutionId, name: `${lender.institutionName} - ${lender.lenderName}` });
      }
    });
    return Array.from(uniqueLenders.values());
  }, [disbursements, lenders]);

  const filteredDisbursements = useMemo(() => {
    let filtered = [...disbursements];
    if (selectedLenderFilter !== "all") {
      filtered = filtered.filter((d) => d.lenderId === selectedLenderFilter);
    }
    if (searchTerm) {
      filtered = filtered.filter((d) =>
        d?.rentalScheduleNumber?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return filtered.sort((a, b) => parseISO(a?.dueDate ?? "").getTime() - parseISO(b?.dueDate ?? "").getTime());
  }, [disbursements, selectedLenderFilter, searchTerm]);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(amount);

  const formatDate = (dateStr: string) => {
    try { return format(parseISO(dateStr), "dd MMM yy"); } catch { return dateStr; }
  };

  const hasSanctionedLenders = useMemo(() => {
    return lenders.some((l) =>
      l.statusHistory.some(
        (e) => (e?.status === "8" || e?.status === "9") && typeof e?.sanctionAmount === "number" && e?.sanctionAmount > 0
      )
    );
  }, [lenders]);

  const isHybrid = requirementType === "Hybrid";

  const getTrancheStatus = (d: DisbursementTranche) => {
    const today = new Date();
  
    const dueDate = d?.last_to_date ? parseISO(d.last_to_date) : null;
    const isMatured = dueDate ? isAfter(today, dueDate) : false;
  
    // ✅ Paid (highest priority)
    if (d?.status === "Rental Paid") {
      return { label: "Paid", variant: "success" as const, icon: CheckCircle2 };
    }
  
    // ✅ Matured → if last_to_date is in past
    if (isMatured) {
      return { label: "Matured", variant: "destructive" as const, icon: Clock };
    }
  
    // ✅ Active → if today or future
    return { label: "Active", variant: "success" as const, icon: CheckCircle2 };
  };

  if (disbursements.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
            <Banknote className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-medium mb-2">No Disbursements Yet</h3>
          <p className="text-sm text-muted-foreground max-w-sm mb-4">
            {isHybrid
              ? "Disbursements will appear here once funds are released."
              : "Disbursements will appear here once funds are released. Each tranche tracks rental schedules and payment due dates."
            }
          </p>
          {hasSanctionedLenders && (
            <Button onClick={() => setAddDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Disbursement
            </Button>
          )}
        </div>
        <AddDisbursementDialog open={addDialogOpen} onOpenChange={setAddDialogOpen} lenders={lenders} disbursements={disbursements} onSubmit={onAddDisbursement} isHybrid={isHybrid} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Top bar: search + filter + add */}
      <div className="flex flex-col sm:flex-row items-stretch gap-3">
        <div className="relative sm:flex-[3]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search by schedule number..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9 h-9" />
        </div>
        <Popover open={lenderSearchOpen} onOpenChange={setLenderSearchOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" role="combobox" aria-expanded={lenderSearchOpen} className="w-full sm:flex-[1] sm:max-w-[280px] justify-between h-9">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <span className="truncate text-xs">{selectedLenderFilter === "all" ? "All Lenders" : lenderOptions.find((l) => l.institutionId === selectedLenderFilter)?.name ?? "Select"}</span>
              </div>
              <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[280px] p-0 z-50 bg-popover text-popover-foreground shadow-md border bg-white">
            <Command>
              <CommandInput placeholder="Search lenders..." />
              <CommandList>
                <CommandEmpty>No lender found.</CommandEmpty>
                <CommandGroup>
                  <CommandItem value="all" onSelect={() => { setSelectedLenderFilter("all"); setLenderSearchOpen(false); }}>
                    <Check className={cn("mr-2 h-4 w-4", selectedLenderFilter === "all" ? "opacity-100" : "opacity-0")} />
                    All Lenders
                  </CommandItem>
                  {lenderOptions.map((l) => (
                    <CommandItem key={l.institutionId} value={l.name} onSelect={() => { setSelectedLenderFilter(l.institutionId); setLenderSearchOpen(false); }}>
                      <Check className={cn("mr-2 h-4 w-4", selectedLenderFilter === l.institutionId ? "opacity-100" : "opacity-0")} />
                      {l.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {hasSanctionedLenders && (
          <Button size="sm" onClick={() => setAddDialogOpen(true)} className="h-9">
            <Plus className="h-4 w-4 mr-1" /> Add Tranche
          </Button>
        )}
      </div>

      {/* Tranches Table */}
      <div className="border rounded-md overflow-hidden">
        {/* Header */}
        <div className="grid grid-cols-[1.2fr_1fr_0.8fr_0.8fr_0.8fr_0.6fr_0.5fr_auto] gap-0 bg-muted/50 border-b font-medium text-muted-foreground uppercase tracking-wider">
          <div className="px-3 py-2">Lender / Schedule</div>
          <div className="px-3 py-2 ">Disbursed</div>
          <div className="px-3 py-2 ">Rental</div>
          <div className="px-3 py-2">Disbursed On</div>
          <div className="px-3 py-2">Due Date</div>
          <div className="px-3 py-2">Status</div>
          <div className="px-3 py-2">NACH</div>
          <div className="px-3 py-2 text-right">Action</div>
        </div>
        {/* Rows */}
        {filteredDisbursements.map((d) => {
          const lender = lenders.find((l) => l.institutionId === d.lenderId);
          const status = getTrancheStatus(d);
          const SIcon = status.icon;
          return (
            <div
              key={`${d.id ?? ""}-${d.lenderId ?? ""}-${d.rentalScheduleNumber ?? ""}-${d.dueDate ?? ""}`}
              className="grid grid-cols-[1.2fr_1fr_0.8fr_0.8fr_0.8fr_0.6fr_0.5fr_auto] gap-0 border-b last:border-b-0 hover:bg-muted/20 transition-colors"
            >
              <div className="px-3 py-2.5 flex items-center gap-2 min-w-0">
                <div className="bg-primary/10 flex items-center justify-center text-primary font-bold shrink-0">
                  {lender?.institutionAvatar ? (
                    <img
                      src={lender.institutionAvatar.startsWith("http") ? lender.institutionAvatar : `${import.meta.env.VITE_API_BASE_URL}/${lender.institutionAvatar}`}
                      alt={lender.institutionName}
                      className="w-[80px] h-[40px] rounded-full object-cover"
                    />
                  ) : (
                    <Building2 className="h-6 w-6 text-primary" />
                  )}
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-medium truncate">{lender?.institutionName ?? "Unknown"}</div>
                  <div className="text-muted-foreground truncate">{'RS.No. ' + d.rentalScheduleNumber}</div>
                </div>
              </div>
              <div className="px-3 py-2.5 flex items-center text-xs font-medium">{formatCurrency(d.disbursedAmount)}</div>
              <div className="px-3 py-2.5 flex items-center text-xs font-medium">{formatCurrency(d.monthlyRental)}</div>
              <div className="px-3 py-2.5 flex items-center text-xs">{formatDate(d.disbursedOn)}</div>
              <div className="px-3 py-2.5 flex items-center text-xs">{formatDate(d?.dueDate ?? "")}</div>
              <div className="px-3 py-2.5 flex items-center">
                <Badge variant={status.variant} className="h-5 px-1.5 gap-0.5">
                  <SIcon className="h-3 w-3" />
                  {status.label}
                </Badge>
              </div>
              <div className="px-3 py-2.5 flex items-center justify-center">
                {d.noaNachActive ? (
                  <Badge variant="outline" className="h-5 px-1">
                    <CreditCard className="h-3 w-3" />
                  </Badge>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </div>
              <div className="px-3 py-2.5 flex items-center justify-end">
                <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setViewDisbursement(d)} title="View">
                  <Eye className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          );
        })}
        {filteredDisbursements.length === 0 && (
          <div className="p-6 text-center text-sm text-muted-foreground">No tranches match your filters.</div>
        )}
      </div>

      <AddDisbursementDialog open={addDialogOpen} onOpenChange={setAddDialogOpen} lenders={lenders} disbursements={disbursements} onSubmit={onAddDisbursement} isHybrid={isHybrid} />
      <ViewDisbursementDialog open={!!viewDisbursement} onOpenChange={(open) => !open && setViewDisbursement(null)} disbursement={viewDisbursement} lender={viewDisbursement ? lenders.find((l) => l.institutionId === viewDisbursement.lenderId) : undefined} />
    </div>
  );
}